import React, { Suspense, useEffect, useRef } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

// Import layout components
import Header from '@components/layout/Header';
import Footer from '@components/layout/Footer';
import ErrorBoundary from '@components/ui/ErrorBoundary';
import Loading from '@components/ui/Loading';
// Phase 7: FloatingIcon (Dino mascot) removed - replaced by FloatingVoiceAssistant
import FloatingVoiceAssistant from '@components/ui/FloatingVoiceAssistant';

// Import hooks
import { useActiveSession } from '@hooks/useActiveSession';

// Import utilities
import { audioManager } from '@utils/audioManager';

// Lazy load pages for better performance
const Home = React.lazy(() => import('@pages/Home'));
const Browse = React.lazy(() => import('@pages/Browse'));
const TopicAnimations = React.lazy(() => import('@pages/TopicAnimations'));
const Player = React.lazy(() => import('@pages/Player'));
const Exercise = React.lazy(() => import('@pages/Exercise'));
const Profile = React.lazy(() => import('@pages/Profile'));
const ProfileRouter = React.lazy(() => import('@components/ProfileRouter'));
const SignIn = React.lazy(() => import('@pages/SignIn'));
const SignUp = React.lazy(() => import('@pages/SignUp'));
const ParentSignUp = React.lazy(() => import('@pages/ParentSignUp'));
const ParentDashboard = React.lazy(() => import('@pages/ParentDashboard'));
const TextbookSelection = React.lazy(() => import('@pages/TextbookSelection'));
const HomeworkAssistant = React.lazy(() => import('@pages/HomeworkAssistant'));
const HomeworkHistory = React.lazy(() => import('@pages/HomeworkHistory'));
const HomeworkValidationResults = React.lazy(() => import('@pages/HomeworkValidationResults'));
const HomeworkReview = React.lazy(() => import('@pages/HomeworkReview'));
const HomeworkVoiceTutor = React.lazy(() => import('@pages/HomeworkVoiceTutor'));
const TopicAnalysis = React.lazy(() => import('@pages/TopicAnalysis'));
const ExerciseNavigation = React.lazy(() => import('@pages/ExerciseNavigation'));
const ExerciseSelection = React.lazy(() => import('@pages/ExerciseSelection'));
const ExerciseHistory = React.lazy(() => import('@pages/ExerciseHistory'));
const ExerciseResult = React.lazy(() => import('@pages/ExerciseResult'));
const ExerciseVoiceTutor = React.lazy(() => import('@pages/ExerciseVoiceTutor'));
const SchoolHomeworkDashboard = React.lazy(() => import('./school-homework-tracking/pages/SchoolHomeworkDashboard'));

// Import stores
import { useAuthStore } from '@stores/authStore';

// Loading fallback component
const PageLoader = () => (
  <div className="min-h-screen bg-gray-50 flex items-center justify-center">
    <Loading size="lg" text="Loading..." />
  </div>
);

// Protected route wrapper
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuthStore();
  
  if (isLoading) {
    return <PageLoader />;
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/signin" replace />;
  }
  
  return children;
};

// Public route wrapper (redirect to home if authenticated)
const PublicRoute = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuthStore();
  
  if (isLoading) {
    return <PageLoader />;
  }
  
  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }
  
  return children;
};

function App() {
  const location = useLocation();
  const previousLocationRef = useRef(location.pathname);
  
  // Detect active session (homework OR exercise) for global voice assistant
  // Phase 1: sessionId can be null - voice assistant is always visible
  const activeSession = useActiveSession();
  const sessionId = activeSession?.sessionId || null;
  const sessionType = activeSession?.sessionType || 'homework';
  const pageContext = activeSession?.pageContext || 'unknown';
  
  // Phase 1: Don't auto-connect - connection will be activated in Phase 3
  const shouldConnectVoiceAgent = false;
  
  // Stop all audio when navigating between pages
  useEffect(() => {
    const previousPath = previousLocationRef.current;
    const currentPath = location.pathname;
    
    // If page actually changed (not just query params)
    if (previousPath !== currentPath) {
      console.log('🚀 Navigation detected:', previousPath, '→', currentPath);
      console.log('🛑 Stopping all audio due to navigation');
      
      // Stop all active audio
      audioManager.stopAll();
    }
    
    previousLocationRef.current = currentPath;
  }, [location.pathname]);
  
  return (
    <>
      <Helmet>
        <title>Dino Learn - Interactive Learning Platform</title>
        <meta name="description" content="Learn through engaging animations and interactive educational content" />
      </Helmet>
      
      <div className="min-h-screen bg-gray-50 flex flex-col">
        <ErrorBoundary>
          {/* Header - shown on all pages */}
          <Header />
          
          {/* Main content area */}
          <main className="flex-1 relative">
            <Suspense fallback={<PageLoader />}>
              <Routes>
                {/* Public routes */}
                <Route 
                  path="/signin" 
                  element={
                    <PublicRoute>
                      <SignIn />
                    </PublicRoute>
                  } 
                />
                <Route 
                  path="/signup/*" 
                  element={
                    <PublicRoute>
                      <SignUp />
                    </PublicRoute>
                  } 
                />
                <Route 
                  path="/parent-signup" 
                  element={
                    <PublicRoute>
                      <ParentSignUp />
                    </PublicRoute>
                  } 
                />
                <Route 
                  path="/parent-dashboard" 
                  element={
                    <ProtectedRoute>
                      <ParentDashboard />
                    </ProtectedRoute>
                  } 
                />
                
                {/* Protected routes */}
                <Route 
                  path="/" 
                  element={
                    <ProtectedRoute>
                      <Home />
                    </ProtectedRoute>
                  } 
                />
                
                {/* Clean slug + ID navigation routes */}
                <Route 
                  path="/browse" 
                  element={
                    <ProtectedRoute>
                      <Browse />
                    </ProtectedRoute>
                  } 
                />
                
                {/* Textbook selection (onboarding) */}
                <Route 
                  path="/textbook-selection" 
                  element={
                    <ProtectedRoute>
                      <TextbookSelection />
                    </ProtectedRoute>
                  } 
                />
                
                {/* Textbook chapters: /textbook-slug/123 */}
                <Route
                  path="/:textbookSlug/:textbookId"
                  element={
                    <ProtectedRoute>
                      <Browse />
                    </ProtectedRoute>
                  }
                />

                {/* Chapter topics: /textbook-slug/123/chapter-slug/456 */}
                <Route
                  path="/:textbookSlug/:textbookId/:chapterSlug/:chapterId"
                  element={
                    <ProtectedRoute>
                      <Browse />
                    </ProtectedRoute>
                  }
                />

                {/* Topic animations: /textbook-slug/123/chapter-slug/456/topic-slug/789 */}
                <Route
                  path="/:textbookSlug/:textbookId/:chapterSlug/:chapterId/:topicSlug/:topicId"
                  element={
                    <ProtectedRoute>
                      <TopicAnimations />
                    </ProtectedRoute>
                  }
                />
                
                {/* Direct content player (for homework recommendations) */}
                <Route
                  path="/play/:contentId"
                  element={
                    <ProtectedRoute>
                      <Player />
                    </ProtectedRoute>
                  }
                />

                {/* Animation player: /textbook-slug/123/chapter-slug/456/topic-slug/789/animation-slug/101 */}
                <Route
                  path="/:textbookSlug/:textbookId/:chapterSlug/:chapterId/:topicSlug/:topicId/:animationSlug/:contentId"
                  element={
                    <ProtectedRoute>
                      <Player />
                    </ProtectedRoute>
                  }
                />
                
                {/* Exercise player */}
                <Route 
                  path="/exercise/:exerciseId" 
                  element={
                    <ProtectedRoute>
                      <Exercise />
                    </ProtectedRoute>
                  } 
                />
                
                {/* New hierarchical exercise route */}
                <Route 
                  path="/exercises/:textbookSlug/:chapterSlug/:exerciseSlug" 
                  element={
                    <ProtectedRoute>
                      <Exercise />
                    </ProtectedRoute>
                  } 
                />
                
                {/* Exercise Navigation Hub */}
                <Route 
                  path="/exercises" 
                  element={
                    <ProtectedRoute>
                      <ExerciseNavigation />
                    </ProtectedRoute>
                  } 
                />
                
                {/* Exercise Selection */}
                <Route 
                  path="/exercises/select" 
                  element={
                    <ProtectedRoute>
                      <ExerciseSelection />
                    </ProtectedRoute>
                  } 
                />
                
                {/* Exercise History */}
                <Route 
                  path="/exercises/history" 
                  element={
                    <ProtectedRoute>
                      <ExerciseHistory />
                    </ProtectedRoute>
                  } 
                />
                
                {/* Exercise Result */}
                <Route 
                  path="/exercises/result/:attemptId" 
                  element={
                    <ProtectedRoute>
                      <ExerciseResult />
                    </ProtectedRoute>
                  } 
                />

                {/* Alternative path for exercise result */}
                <Route 
                  path="/exercise/result/:attemptId" 
                  element={
                    <ProtectedRoute>
                      <ExerciseResult />
                    </ProtectedRoute>
                  } 
                />

                {/* Exercise Voice Tutor */}
                <Route 
                  path="/exercise/voice-tutor/:attemptId/:partId" 
                  element={
                    <ProtectedRoute>
                      <ExerciseVoiceTutor />
                    </ProtectedRoute>
                  } 
                />
                
                {/* User profile - smart routing based on user type */}
                <Route 
                  path="/profile" 
                  element={
                    <ProtectedRoute>
                      <ProfileRouter />
                    </ProtectedRoute>
                  }
                />
                
                {/* Homework Assistant */}
                <Route
                  path="/homework"
                  element={
                    <ProtectedRoute>
                      <HomeworkAssistant />
                    </ProtectedRoute>
                  }
                />

                {/* Homework Sessions - Resume existing sessions */}
                <Route
                  path="/homework/sessions"
                  element={
                    <ProtectedRoute>
                      <HomeworkAssistant />
                    </ProtectedRoute>
                  }
                />

                {/* Homework New Session - Create new session */}
                <Route
                  path="/homework/new"
                  element={
                    <ProtectedRoute>
                      <HomeworkAssistant />
                    </ProtectedRoute>
                  }
                />

                {/* Homework Validation Results */}
                <Route 
                  path="/homework/validation-results/:sessionId" 
                  element={
                    <ProtectedRoute>
                      <HomeworkValidationResults />
                    </ProtectedRoute>
                  }
                />
                
              {/* Homework Voice Tutor - Voice-guided error correction */}
              <Route 
                path="/homework/voice-tutor/:sessionId/:problemId" 
                element={
                  <ProtectedRoute>
                    <HomeworkVoiceTutor />
                  </ProtectedRoute>
                }
              />
                
              {/* Homework Problem Review - Read-only review of validated problems */}
              <Route 
                path="/homework/review/:sessionId" 
                element={
                  <ProtectedRoute>
                    <HomeworkReview />
                  </ProtectedRoute>
                }
              />
              
              {/* Topic Analysis */}
              <Route 
                  path="/homework/topic-analysis/:sessionId" 
                  element={
                    <ProtectedRoute>
                      <TopicAnalysis />
                    </ProtectedRoute>
                  }
                />
                
                {/* Homework History */}
                <Route
                  path="/homework-history"
                  element={
                    <ProtectedRoute>
                      <HomeworkHistory />
                    </ProtectedRoute>
                  }
                />

                {/* School Homework Tracking - Standalone module */}
                <Route
                  path="/school-homework"
                  element={
                    <ProtectedRoute>
                      <SchoolHomeworkDashboard />
                    </ProtectedRoute>
                  }
                />
                
                {/* Fallback routes */}
                <Route path="/404" element={<NotFound />} />
                <Route path="*" element={<Navigate to="/404" replace />} />
              </Routes>
            </Suspense>
          </main>
          
          {/* Footer - shown on all pages */}
          <Footer />
          
          {/* Phase 7: Dino mascot removed - FloatingVoiceAssistant now handles all functionality */}
          {/* Global Voice Assistant - Always visible, draggable, with animations & wake word */}
          <FloatingVoiceAssistant 
            sessionId={sessionId}
            sessionType={sessionType}
            pageContext={pageContext}
            shouldConnect={shouldConnectVoiceAgent}
          />
        </ErrorBoundary>
      </div>
    </>
  );
}

// 404 Not Found component
const NotFound = () => (
  <div className="min-h-[60vh] flex items-center justify-center p-4">
    <div className="text-center">
      <div className="w-24 h-24 mx-auto mb-6 bg-gray-100 rounded-full flex items-center justify-center">
        <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.172 16.172a4 4 0 015.656 0M9 12h6m-6-4h6m2 5.291A7.962 7.962 0 0112 15c-2.34 0-4.467-.881-6.08-2.33M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 12.728" />
        </svg>
      </div>
      <h1 className="text-3xl font-bold text-gray-900 mb-2">Page Not Found</h1>
      <p className="text-gray-600 mb-6">
        The page you're looking for doesn't exist or has been moved.
      </p>
      <div className="space-x-4">
        <button
          onClick={() => window.history.back()}
          className="btn-outline"
        >
          Go Back
        </button>
        <a href="/" className="btn-primary">
          Go Home
        </a>
      </div>
    </div>
  </div>
);

export default App;